import React, { useEffect, useRef, useState } from "react";
import { fromUnixTime, differenceInSeconds, subMinutes, isBefore } from "date-fns";

type UserClassType = {
  startTime?: number | string | Date;
  endTime?: number | string | Date;
  userClassHash?: { classHash?: string } | null;
  class?: {
    liveStreamUrl?: string | null;
    liveStreamClass?: boolean;
    liveStreamPassword?: string | null;
  } | null;
};

type Props = {
  userClass: UserClassType;
};

export default function LiveStreamJoinButton({
  userClass,
}: Props) {
  const [canJoin, setCanJoin] = useState(false);
  const [timeToClass, setTimeToClass] = useState<number>(0);
  const [endTimePast, setEndTimePast] = useState<boolean>(true);
  const timerRef = useRef<number | null>(null);

  // Helper: convert unix seconds or ms (or Date/ISO) -> Date
  const toDate = (t?: number | string | Date): Date => {
    if (!t) return new Date(0);
    if (t instanceof Date) return t;
    const n = typeof t === "string" ? Number(t) : (t as number);
    if (!Number.isNaN(n)) {
      // treat > 1e12 as ms else seconds
      return n > 1e12 ? new Date(n) : fromUnixTime(n);
    }
    // fallback: try Date parse
    return new Date(String(t));
  };

  useEffect(() => {
    const _class = userClass?.class;
    const end = toDate(userClass?.endTime);
    const now = new Date();
    const isEndPast = isBefore(end, now);
    setEndTimePast(isEndPast);

    // only start timer if it's a live stream class and hasn't ended
    if (_class?.liveStreamClass && !isEndPast) {
      runImmediateCountDown();
      if (!timerRef.current) {
        timerRef.current = window.setInterval(runImmediateCountDown, 1000);
      }
    }

    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    // run when userClass changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userClass]);

  const runImmediateCountDown = () => {
    const start = toDate(userClass?.startTime);
    const end = toDate(userClass?.endTime);
    const now = new Date();

    const joinAvailableStart = subMinutes(start, 15);
    const secondsTo = differenceInSeconds(joinAvailableStart, now);
    const joinable = secondsTo <= 0 || isBefore(joinAvailableStart, now);
    setCanJoin(joinable);
    setTimeToClass(secondsTo > 0 ? secondsTo : 0);

    // clear timer when reached 0 to avoid negative ticks
    if (secondsTo <= 0 && timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // mark end time state
    setEndTimePast(isBefore(end, now));
  };

  const secondsToDhms = (secondsInput: number) => {
    let seconds = Number(secondsInput);
    if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor((seconds % (3600 * 24)) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    const dDisplay = d > 0 ? `${d}${d === 1 ? " día, " : " días, "}` : "";
    const hDisplay = h > 0 ? `${h}${h === 1 ? " hora, " : " horas, "}` : "";
    const mDisplay = m > 0 ? `${m}${m === 1 ? " minuto, " : " minutos, "}` : "0 minutos, ";
    const sDisplay = s > 0 ? `${s}${s === 1 ? " segundo" : " segundos"}` : "0 segundos";
    return `${dDisplay}${hDisplay}${mDisplay}${sDisplay}`;
  };

  const buildJoinUrl = (): string | null => {
    const classHash = userClass?.userClassHash?.classHash;
    if (classHash) return `/user/unirme/${encodeURIComponent(classHash)}`;
    return null;
  };

  const handleJoin = async (e: React.MouseEvent) => {
    e.preventDefault();
    const joinUrl = buildJoinUrl();
    if (!joinUrl) return;

    window.open(joinUrl, "_blank", "noopener,noreferrer");
  };

  if (!userClass?.class?.liveStreamClass || endTimePast) {
    return null;
  }

  return (
    <div className="text-center mt-2">
      <button
        onClick={handleJoin}
        disabled={!canJoin}
        aria-disabled={!canJoin}
        className={`
            w-full max-w-lg mx-auto py-2 px-4 rounded font-semibold focus:outline-none transition-colors 
            ${canJoin ? " bg-holos-dark hover:bg-holos-dark/80 text-white cursor-pointer" : " bg-blue-200 text-gray-600 cursor-not-allowed"}
        `}
      >
        {!canJoin ? `Unirme en ${secondsToDhms(timeToClass)}` : "Unirme ahora"}
      </button>

      {userClass?.class?.liveStreamPassword && (
        <div className="mt-2 text-sm font-medium text-gray-700">
          Contraseña de clase: <span className="font-semibold">{userClass.class.liveStreamPassword}</span>
          <div className="text-xs text-gray-500 mt-1">Si no puedes ingresar, usa el link del correo.</div>
        </div>
      )}
    </div>
  );
}
